import React from 'react'

import { BirthdayCatalogItem } from '@facades/birthday'
import { getEndpoint } from '@utils/getEndpoint'

import {
  ChoiceCard,
  ChoiceList,
  ExtraOrderContent,
  ExtraOrderGroup,
  ExtraOrderSummary,
  FoodSectionHeader,
  OrderDetails,
  OrderGroup,
  OrderGroupHeader,
  OrderImage,
  OrderList,
  OrderPrice,
  OrderRow,
  OrderTotal,
  OptionImage,
  PackageSection,
  QuantityButton,
  QuantityControl,
  Recommendation,
  RecommendationCount,
  RecommendationIcon,
  SelectionStatus,
  InfoPanel,
} from './birthdayPackageSelection.styled'

const money = (value: number) =>
  `${new Intl.NumberFormat('cs-CZ').format(value)} Kč`

type BirthdayPackageSelectionProps = {
  packages: BirthdayCatalogItem[]
  extras: BirthdayCatalogItem[]
  cakes: BirthdayCatalogItem[]
  decorations: BirthdayCatalogItem[]
  pizzaFlavorOptions: Array<{ id: string; name: string }>
  ownCakePrice: number
  candlePrice: number
  drinksMenuUrl?: string
  children: number
  packageQuantities: Record<string, number>
  pizzaFlavors: Record<string, number>
  extraQuantities: Record<string, number>
  cake: {
    mode: 'none' | 'own' | 'ours'
    items: Record<string, number>
  }
  decor: 'none' | 'mini' | 'wizard' | 'pink'
  candles: number
  onPackageChange: (itemId: string, quantity: number) => void
  onExtraChange: (itemId: string, quantity: number) => void
  onPizzaFlavorChange: (itemId: string, quantity: number) => void
  onCakeModeChange: (mode: 'none' | 'own' | 'ours') => void
  onCakeItemChange: (itemId: string, quantity: number) => void
  onDecorChange: (decor: 'none' | 'mini' | 'wizard' | 'pink') => void
  onCandlesChange: (quantity: number) => void
}

const imageUrl = (image: string) => {
  if (image.startsWith('http')) return image
  if (image.startsWith('/birthday/')) return `${getEndpoint()}${image}`
  const path = image.replace(/^\/+/, '')
  const assetBaseUrl = window.birthdayAssetBaseUrl?.replace(/\/+$/, '')
  return assetBaseUrl ? `${assetBaseUrl}/${path}` : `/${path}`
}

const OrderItems = ({
  items,
  quantities,
  featured = false,
  onChange,
}: {
  items: BirthdayCatalogItem[]
  quantities: Record<string, number>
  featured?: boolean
  onChange: (itemId: string, quantity: number) => void
}) => (
  <OrderList featured={featured}>
    {items.map((item) => {
      const quantity = quantities[item.id] ?? 0
      const description =
        item.contents?.join(' · ') ||
        [item.unit, item.category].filter(Boolean).join(' · ')
      const priceLabel = featured ? 'Cena balíčku' : item.unit ?? 'Cena za kus'
      return (
        <OrderRow key={item.id} featured={featured} selected={quantity > 0}>
          {featured && item.image && (
            <OrderImage
              src={imageUrl(item.image)}
              alt={item.name}
              loading="lazy"
            />
          )}
          <OrderDetails>
            <span>
              {item.servings
                ? `Pro ${item.servings} dětí`
                : item.category ?? 'Jídlo navíc'}
            </span>
            <strong>{item.name}</strong>
            {description && <small>{description}</small>}
          </OrderDetails>
          <OrderPrice>
            <small>{priceLabel}</small>
            <strong>{money(item.price)}</strong>
          </OrderPrice>
          <QuantityControl aria-label={`Množství: ${item.name}`}>
            <QuantityButton
              type="button"
              aria-label={`Odebrat ${item.name}`}
              disabled={quantity === 0}
              onClick={() => onChange(item.id, Math.max(0, quantity - 1))}
            >
              −
            </QuantityButton>
            <strong aria-live="polite">{quantity}</strong>
            <QuantityButton
              type="button"
              aria-label={`Přidat ${item.name}`}
              onClick={() => onChange(item.id, quantity + 1)}
            >
              +
            </QuantityButton>
          </QuantityControl>
          {quantity > 0 && (
            <OrderTotal>
              Celkem <strong>{money(item.price * quantity)}</strong>
            </OrderTotal>
          )}
        </OrderRow>
      )
    })}
  </OrderList>
)

export const BirthdayPackageSelection = ({
  packages,
  extras,
  cakes,
  decorations,
  pizzaFlavorOptions,
  ownCakePrice,
  candlePrice,
  drinksMenuUrl,
  children,
  packageQuantities,
  pizzaFlavors,
  extraQuantities,
  cake,
  decor,
  candles,
  onPackageChange,
  onExtraChange,
  onPizzaFlavorChange,
  onCakeModeChange,
  onCakeItemChange,
  onDecorChange,
  onCandlesChange,
}: BirthdayPackageSelectionProps) => {
  const recommendedPackages = Math.ceil(children / 5)
  const selectedPackages = Object.values(packageQuantities).reduce(
    (sum, quantity) => sum + quantity,
    0
  )
  const recommendationMet = selectedPackages >= recommendedPackages
  const selectedExtras = Object.values(extraQuantities).reduce(
    (sum, quantity) => sum + quantity,
    0
  )
  const selectedExtrasTotal = extras.reduce(
    (sum, item) => sum + item.price * (extraQuantities[item.id] ?? 0),
    0
  )
  const expectedPizzas = (packageQuantities.pizza ?? 0) * 3
  const selectedPizzas = Object.values(pizzaFlavors).reduce(
    (sum, quantity) => sum + quantity,
    0
  )
  const pizzaSelectionComplete = selectedPizzas === expectedPizzas

  return (
    <PackageSection>
      <FoodSectionHeader>
        <span>3. Občerstvení</span>
        <h2>Menu pro narozeninovou oslavu</h2>
        <p>
          Vyberte základní balíčky a doplňte je podle chuti. Občerstvení se
          platí na místě.
        </p>
      </FoodSectionHeader>
      <Recommendation complete={recommendationMet}>
        <RecommendationIcon complete={recommendationMet} aria-hidden="true" />
        <div>
          <strong>
            {recommendationMet
              ? 'Doporučené množství je splněno'
              : `Doporučujeme ${recommendedPackages} balíčky pro ${children} dětí`}
          </strong>
          <span>
            {recommendationMet
              ? `Máte vybráno ${selectedPackages} z doporučených ${recommendedPackages} balíčků.`
              : 'Jeden balíček je připraven přibližně pro pět dětí.'}
          </span>
        </div>
        <RecommendationCount>
          {selectedPackages}/{recommendedPackages}
        </RecommendationCount>
      </Recommendation>
      <OrderGroup>
        <OrderGroupHeader>
          <div>
            <span>Hlavní nabídka</span>
            <h3>Základní balíčky</h3>
          </div>
          <small>Kompletní menu přibližně pro 5 dětí</small>
        </OrderGroupHeader>
        <OrderItems
          featured
          items={packages}
          quantities={packageQuantities}
          onChange={onPackageChange}
        />
      </OrderGroup>
      {expectedPizzas > 0 && (
        <OrderGroup>
          <OrderGroupHeader>
            <div>
              <span>Součást pizza balíčku</span>
              <h3>Vyberte příchutě pizzy</h3>
            </div>
            <small>Každý balíček obsahuje 3 pizzy</small>
          </OrderGroupHeader>
          <SelectionStatus complete={pizzaSelectionComplete} role="status">
            Vybráno {selectedPizzas} z {expectedPizzas} pizz
          </SelectionStatus>
          <OrderList>
            {pizzaFlavorOptions.map((flavor) => {
              const quantity = pizzaFlavors[flavor.id] ?? 0
              return (
                <OrderRow key={flavor.id} selected={quantity > 0}>
                  <OrderDetails>
                    <span>Pizza</span>
                    <strong>{flavor.name}</strong>
                  </OrderDetails>
                  <QuantityControl aria-label={`Množství: ${flavor.name}`}>
                    <QuantityButton
                      type="button"
                      aria-label={`Odebrat ${flavor.name}`}
                      disabled={quantity === 0}
                      onClick={() =>
                        onPizzaFlavorChange(flavor.id, quantity - 1)
                      }
                    >
                      −
                    </QuantityButton>
                    <strong>{quantity}</strong>
                    <QuantityButton
                      type="button"
                      aria-label={`Přidat ${flavor.name}`}
                      disabled={selectedPizzas >= expectedPizzas}
                      onClick={() =>
                        onPizzaFlavorChange(flavor.id, quantity + 1)
                      }
                    >
                      +
                    </QuantityButton>
                  </QuantityControl>
                </OrderRow>
              )
            })}
          </OrderList>
        </OrderGroup>
      )}
      <ExtraOrderGroup>
        <ExtraOrderSummary>
          <div>
            <span>Doplňková nabídka</span>
            <h3>Jídlo navíc</h3>
          </div>
          <small>
            {selectedExtras
              ? `Vybráno ${selectedExtras} · ${money(selectedExtrasTotal)}`
              : 'Volitelné jednotlivé porce a přílohy'}
          </small>
        </ExtraOrderSummary>
        <ExtraOrderContent>
          <OrderItems
            items={extras}
            quantities={extraQuantities}
            onChange={onExtraChange}
          />
        </ExtraOrderContent>
      </ExtraOrderGroup>
      <OrderGroup>
        <OrderGroupHeader>
          <div>
            <span>Slavnostní dort</span>
            <h3>Dort</h3>
          </div>
        </OrderGroupHeader>
        <ChoiceList>
          {[
            { id: 'none' as const, name: 'Bez dortu', detail: '0 Kč' },
            {
              id: 'own' as const,
              name: 'Vlastní dort',
              detail: money(ownCakePrice),
            },
            {
              id: 'ours' as const,
              name: 'Objednat dort',
              detail: 'Vyberte příchuť',
            },
          ].map((option) => (
            <ChoiceCard key={option.id} selected={cake.mode === option.id}>
              <input
                type="radio"
                name="birthday-cake-mode"
                checked={cake.mode === option.id}
                onChange={() => onCakeModeChange(option.id)}
              />
              <span>
                <strong>{option.name}</strong>
                <small>{option.detail}</small>
              </span>
            </ChoiceCard>
          ))}
        </ChoiceList>
        {cake.mode === 'ours' && (
          <OrderItems
            items={cakes}
            quantities={cake.items}
            onChange={onCakeItemChange}
          />
        )}
      </OrderGroup>
      <OrderGroup>
        <OrderGroupHeader>
          <div>
            <span>Výzdoba prostoru</span>
            <h3>Dekorace</h3>
          </div>
        </OrderGroupHeader>
        <ChoiceList>
          {decorations.map((item) => {
            const id = item.id as BirthdayPackageSelectionProps['decor']
            return (
              <ChoiceCard key={item.id} selected={decor === id}>
                <input
                  type="radio"
                  name="birthday-decor"
                  checked={decor === id}
                  onChange={() => onDecorChange(id)}
                />
                {item.image && (
                  <OptionImage src={imageUrl(item.image)} alt="" />
                )}
                <span>
                  <strong>{item.name}</strong>
                  <small>{money(item.price)}</small>
                </span>
              </ChoiceCard>
            )
          })}
        </ChoiceList>
      </OrderGroup>
      <OrderGroup>
        <OrderGroupHeader>
          <div>
            <span>Doplňky</span>
            <h3>Svíčky</h3>
          </div>
          <small>{money(candlePrice)} za kus</small>
        </OrderGroupHeader>
        <QuantityControl aria-label="Množství: Svíčky">
          <QuantityButton
            type="button"
            aria-label="Odebrat svíčku"
            disabled={candles === 0}
            onClick={() => onCandlesChange(Math.max(0, candles - 1))}
          >
            −
          </QuantityButton>
          <strong>{candles}</strong>
          <QuantityButton
            type="button"
            aria-label="Přidat svíčku"
            onClick={() => onCandlesChange(candles + 1)}
          >
            +
          </QuantityButton>
        </QuantityControl>
      </OrderGroup>
      <InfoPanel>
        Nápoje nejsou zahrnuté v předběžné kalkulaci.
        {drinksMenuUrl && (
          <a href={drinksMenuUrl} target="_blank" rel="noreferrer">
            Zobrazit nabídku nápojů
          </a>
        )}
      </InfoPanel>
    </PackageSection>
  )
}
